<!--
  Visual hierarchy (AGENTS.md §Visual hierarchy)
  Question: "What exactly will commit create, and what won't it?"
  Answer:   the preview banner - a StatusTag stating plainly that nothing exists yet - sits
            above everything else, since mistaking a preview for a done deal is the one error
            this screen cannot afford. Counters (total/valid/invalid/duplicate/to commit) come
            right under it as the answer's own numbers.
  Context:  the row table - every valid row is directly editable in place (date, description,
            amount, counter account, excluded), an invalid row shows its own reason and nothing
            else, since there is no preview entry behind it to edit.
  Controls: the counter-account SearchableSelect and the excluded checkbox are ordinary bordered
            controls; the one --color-action control on this whole screen is the commit button
            on the page that hosts this table, not anything here.
-->
<script setup lang="ts">
import type {
  EditImportPreviewEntryInput,
  ImportJobStatus,
  ImportPreviewEntry,
  ImportRawRow,
} from '../internal/types.ts'
import { Card, StatusTag } from '@taler/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import ImportPreviewEntryRow from '@/features/imports/components/ImportPreviewEntryRow.vue'

const props = defineProps<{
  rawRows: readonly ImportRawRow[]
  previewEntries: readonly ImportPreviewEntry[]
  jobStatus: ImportJobStatus
}>()

const emit = defineEmits<{
  save: [{ entryId: string; input: EditImportPreviewEntryInput }]
  viewDuplicate: [string]
}>()

const { t } = useI18n()

const disabled = computed(() => props.jobStatus !== 'OPEN')

const previewByRawRowId = computed(
  () => new Map(props.previewEntries.map((entry) => [entry.rawRowId, entry])),
)
const orderedRawRows = computed(() => [...props.rawRows].sort((a, b) => a.rowNumber - b.rowNumber))
const invalidCount = computed(() => props.rawRows.filter((row) => row.parseError !== undefined).length)
const duplicateCount = computed(
  () => props.previewEntries.filter((entry) => entry.duplicateOfEntryId !== undefined).length,
)
const toCommitCount = computed(() => props.previewEntries.filter((entry) => !entry.excluded).length)

function onSave(entryId: string, input: EditImportPreviewEntryInput): void {
  emit('save', { entryId, input })
}
</script>

<template>
  <div class="flex flex-col gap-[var(--space-block)]">
    <StatusTag tone="neutral">{{ t('imports.preview.notice') }}</StatusTag>

    <dl class="grid grid-cols-2 gap-[var(--space-cell)] sm:grid-cols-4">
      <Card padding="cell">
        <dt class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
          {{ t('imports.preview.totalRows') }}
        </dt>
        <dd class="text-[length:var(--text-step-5)] font-semibold text-[color:var(--color-text)]">
          {{ rawRows.length }}
        </dd>
      </Card>
      <Card padding="cell">
        <dt class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
          {{ t('imports.preview.invalidRows') }}
        </dt>
        <dd class="text-[length:var(--text-step-5)] font-semibold text-[color:var(--color-text)]">
          {{ invalidCount }}
        </dd>
      </Card>
      <Card padding="cell">
        <dt class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
          {{ t('imports.preview.duplicateRows') }}
        </dt>
        <dd class="text-[length:var(--text-step-5)] font-semibold text-[color:var(--color-text)]">
          {{ duplicateCount }}
        </dd>
      </Card>
      <Card padding="cell">
        <dt class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
          {{ t('imports.preview.toCommit') }}
        </dt>
        <dd class="text-[length:var(--text-step-5)] font-semibold text-[color:var(--color-text)]">
          {{ toCommitCount }}
        </dd>
      </Card>
    </dl>

    <Card padding="none" class="overflow-x-auto">
      <table class="w-full min-w-[48rem] border-collapse">
        <thead>
          <tr class="table-head">
            <th class="px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
              {{ t('imports.preview.date') }}
            </th>
            <th class="px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
              {{ t('imports.preview.description') }}
            </th>
            <th class="px-[var(--space-cell)] py-[var(--space-inline)] text-right" scope="col">
              {{ t('imports.preview.amount') }}
            </th>
            <th class="px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
              {{ t('imports.preview.counterAccount') }}
            </th>
            <th class="px-[var(--space-cell)] py-[var(--space-inline)] text-center" scope="col">
              {{ t('imports.preview.excluded') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-for="row in orderedRawRows" :key="row.id">
            <tr v-if="row.parseError !== undefined" class="border-b border-[color:var(--color-border)]">
              <td
                class="px-[var(--space-cell)] py-[var(--space-cell)] tabular-nums text-[color:var(--color-text-muted)]"
              >
                {{ t('imports.preview.rowNumber', { number: row.rowNumber }) }}
              </td>
              <td colspan="4" class="px-[var(--space-cell)] py-[var(--space-cell)]">
                <StatusTag tone="danger">{{ row.parseError }}</StatusTag>
              </td>
            </tr>
            <ImportPreviewEntryRow
              v-else-if="previewByRawRowId.get(row.id) !== undefined"
              :entry="previewByRawRowId.get(row.id)!"
              :disabled="disabled"
              @save="(input) => onSave(previewByRawRowId.get(row.id)!.id, input)"
              @view-duplicate="(entryId) => emit('viewDuplicate', entryId)"
            />
          </template>
        </tbody>
      </table>
    </Card>
  </div>
</template>
